import { camelCase, sumBy } from "lodash";
import { createSelector } from "reselect";

import {
  TEMPORARY_BUSINESS_WARNING,
  ConsignmentWarnings,
  SaleLotWarning,
  WarningLevel,
} from "constants/sale";
import { Species } from "constants/species";
import { userTypes } from "constants/users";

import { EMPTY_ARRAY, EMPTY_OBJECT } from "lib";

import { getLivestockSaleId, getSaleyardName } from "lib/navigation";

import { selectSaleyardScanEidsByConsignmentIdLookup } from "./consignmentsCalculated";
import {
  getCurrentSale,
  selectCurrentDeploymentSaleIdsList,
} from "./currentSale";
import { selectDeploymentByDeploymentSaleIdLookup } from "./deployments";
import {
  getHasExceptionsByDeploymentSaleId,
  selectHasUnregisteredEidsByConsignmentIdLookup,
  selectIsScanRateCompliantByConsignmentIdLookup,
} from "./exceptions";
import {
  selectConsignmentIdsByDeploymentSaleIdLookup,
  selectSaleLotIdsByAuctionPenIdLookup,
} from "./indexes";
import { selectUsedTemporaryBusinessesInCurrentSale } from "./temporaryBusinesses";
import {
  getActiveRole,
  getSaleLots,
  getVendorSplitSaleLots,
  getVendorSplitConsignments,
  getConsignments,
  getAuctionPens,
  getBusinesses,
} from "./root";
import {
  selectDeploymentIdBySaleLotIdLookup,
  selectSaleLotIdsByDeploymentSaleIdLookup,
  selectHasUnregisteredEidsBySaleLotIdLookup,
} from "./saleLotsCalculated";
import { selectSingleWeighWeightBySaleLotIdLookup } from "./sales";
import { selectAreWeightAndDressingFeaturesEnabled } from "./saleSubTypes";
import { selectValidationOverridesByDeploymentIdLookup } from "./validations";

const isWarning = (validation, saleLot) =>
  validation &&
  [WarningLevel.WARNING, WarningLevel.WARNING_AND_BLOCK].includes(
    validation.validationLevel,
  ) &&
  saleLot.quantity > validation.headThreshold;

export const selectHasAgeWarningBySaleLotId = createSelector(
  [
    getSaleLots,
    selectDeploymentIdBySaleLotIdLookup,
    getCurrentSale,
    selectValidationOverridesByDeploymentIdLookup,
  ],
  (saleLots, deploymentIdBySaleLotId, currentSale, deploymentValidations) =>
    Object.entries(saleLots).reduce((acc, [saleLotId, saleLot]) => {
      const deploymentId = deploymentIdBySaleLotId[saleLotId];
      const validations = deploymentValidations[deploymentId];
      const isCattleSale = currentSale.species_id === Species.CATTLE;
      acc[saleLotId] =
        isCattleSale &&
        isWarning(validations?.missingAge, saleLot) &&
        !saleLot.age_id;
      return acc;
    }, {}),
);

export const selectHasBreedWarningBySaleLotId = createSelector(
  [
    getSaleLots,
    selectDeploymentIdBySaleLotIdLookup,
    selectValidationOverridesByDeploymentIdLookup,
  ],
  (saleLots, deploymentIdBySaleLotId, deploymentValidations) =>
    Object.entries(saleLots).reduce((acc, [saleLotId, saleLot]) => {
      const deploymentId = deploymentIdBySaleLotId[saleLotId];
      const validations = deploymentValidations[deploymentId];
      acc[saleLotId] =
        isWarning(validations?.missingBreed, saleLot) && !saleLot.breed_id;
      return acc;
    }, {}),
);

export const selectHasSexWarningBySaleLotId = createSelector(
  [
    getSaleLots,
    selectDeploymentIdBySaleLotIdLookup,
    selectValidationOverridesByDeploymentIdLookup,
  ],
  (saleLots, deploymentIdBySaleLotId, deploymentValidations) =>
    Object.entries(saleLots).reduce((acc, [saleLotId, saleLot]) => {
      const deploymentId = deploymentIdBySaleLotId[saleLotId];
      const validations = deploymentValidations[deploymentId];
      acc[saleLotId] =
        isWarning(validations?.missingSex, saleLot) && !saleLot.sex_id;
      return acc;
    }, {}),
);

export const selectHasVendorSplitsQuantityWarningBySaleLotId = createSelector(
  [getSaleLots, getVendorSplitSaleLots],
  (saleLots, vendorSplitLots) =>
    Object.entries(saleLots).reduce((acc, [saleLotId, saleLot]) => {
      const saleLotVendorSplits = Object.values(vendorSplitLots).filter(
        sl => sl.parentId === saleLotId,
      );
      const saleLotVendorSplitsQuantity = sumBy(
        saleLotVendorSplits,
        "quantity",
      );
      acc[saleLotId] =
        saleLotVendorSplits.length > 0 &&
        saleLot.quantity !== saleLotVendorSplitsQuantity;
      return acc;
    }, {}),
);

export const selectHasVendorSplitsMissingVendorWarningBySaleLotId =
  createSelector(
    [
      getSaleLots,
      getConsignments,
      getVendorSplitSaleLots,
      getVendorSplitConsignments,
    ],
    (saleLots, consignments, vendorSplitLots, vendorSplitConsignments) =>
      Object.entries(saleLots).reduce((acc, [saleLotId, saleLot]) => {
        const saleLotVendorSplits = Object.values(vendorSplitLots).filter(
          sl => sl.parentId === saleLotId,
        );
        const vendorSplitLotsVendorIds = saleLotVendorSplits.map(
          svs => vendorSplitConsignments[svs.consignment_id]?.vendor_id,
        );

        acc[saleLotId] =
          saleLotVendorSplits.length > 0 &&
          !vendorSplitLotsVendorIds.includes(
            consignments[saleLot.consignment_id]?.vendor_id,
          );
        return acc;
      }, {}),
  );

export const selectHasWeightRangeWarningBySaleLotId = createSelector(
  [
    getSaleLots,
    selectDeploymentIdBySaleLotIdLookup,
    selectValidationOverridesByDeploymentIdLookup,
    getCurrentSale,
    selectAreWeightAndDressingFeaturesEnabled,
  ],
  (
    saleLots,
    deploymentIdBySaleLotId,
    deploymentValidations,
    sale,
    featureEnabled,
  ) =>
    Object.entries(saleLots).reduce((acc, [saleLotId, saleLot]) => {
      const deploymentId = deploymentIdBySaleLotId[saleLotId];
      const validations = deploymentValidations[deploymentId];
      const isSheepSale = sale?.species_id === Species.SHEEP;
      acc[saleLotId] =
        isWarning(validations?.missingEstimatedWeight, saleLot) &&
        !saleLot.estimatedAverageWeightId &&
        saleLot.quantity !== 1 &&
        isSheepSale &&
        featureEnabled;
      return acc;
    }, {}),
);

export const getHasWeightRangeWarningBySaleLotId = lotId => state =>
  selectHasWeightRangeWarningBySaleLotId(state)[lotId];

export const selectHasDentitionWarningBySaleLotId = createSelector(
  [
    getSaleLots,
    selectDeploymentIdBySaleLotIdLookup,
    selectValidationOverridesByDeploymentIdLookup,
  ],
  (saleLots, deploymentIdBySaleLotId, deploymentValidations) =>
    Object.entries(saleLots).reduce((acc, [saleLotId, saleLot]) => {
      const deploymentId = deploymentIdBySaleLotId[saleLotId];
      const validations = deploymentValidations[deploymentId];
      acc[saleLotId] =
        isWarning(validations?.missingDentition, saleLot) &&
        !saleLot.dentitionId;
      return acc;
    }, {}),
);

export const getHasDentitionWarningBySaleLotId = lotId => state =>
  selectHasDentitionWarningBySaleLotId(state)[lotId];

export const selectHasDentitionWarningByAuctionPenId = createSelector(
  [
    getAuctionPens,
    selectSaleLotIdsByAuctionPenIdLookup,
    selectHasDentitionWarningBySaleLotId,
  ],
  (auctionPens, saleLotIdsByPen, hasDentitionWarningBySaleLotId) => {
    return Object.entries(auctionPens).reduce(
      (acc, [auctionPenId, _ignored]) => {
        const saleLotIds = saleLotIdsByPen[auctionPenId] || [];
        const hasWarning = saleLotIds.some(
          id => hasDentitionWarningBySaleLotId[id],
        );
        acc[auctionPenId] = hasWarning;
        return acc;
      },
      {},
    );
  },
);

export const getHasDentitionWarningByAuctionPenId = penId => state =>
  selectHasDentitionWarningByAuctionPenId(state)[penId];

export const selectHasWeightRangeWarningByAuctionPenId = createSelector(
  [
    getAuctionPens,
    selectSaleLotIdsByAuctionPenIdLookup,
    selectHasWeightRangeWarningBySaleLotId,
  ],
  (auctionPens, saleLotIdsByPen, hasWeightRangeWarningBySaleLotId) => {
    return Object.entries(auctionPens).reduce(
      (acc, [auctionPenId, _ignored]) => {
        const saleLotIds = saleLotIdsByPen[auctionPenId] || [];
        const hasWarning = saleLotIds.some(
          id => hasWeightRangeWarningBySaleLotId[id],
        );
        acc[auctionPenId] = hasWarning;
        return acc;
      },
      {},
    );
  },
);

export const getHasWeightRangeWarningByAuctionPenId = penId => state =>
  selectHasDentitionWarningByAuctionPenId(state)[penId];

export const selectNASFieldWarningsBySaleLotId = createSelector(
  [
    getSaleLots,
    selectHasDentitionWarningBySaleLotId,
    selectHasWeightRangeWarningBySaleLotId,
  ],
  (saleLots, dentitionWarningsBySaleLotId, weightRangeWarningsBySaleLotId) => {
    return Object.entries(saleLots).reduce((acc, [saleLotId, _ignored]) => {
      const warnings = [];
      const dentitionWarning = dentitionWarningsBySaleLotId[saleLotId];
      if (dentitionWarning) {
        warnings.push("Dentition");
      }
      const weightRangeWarning = weightRangeWarningsBySaleLotId[saleLotId];
      if (weightRangeWarning) {
        warnings.push("Weight Range");
      }
      acc[saleLotId] = warnings;
      return acc;
    }, {});
  },
);

export const getNASFieldWarningsBySaleLotId = lotId => state =>
  selectNASFieldWarningsBySaleLotId(state)[lotId] || [];

export const selectNASFieldWarningsByAuctionPenId = createSelector(
  [
    getAuctionPens,
    selectHasDentitionWarningByAuctionPenId,
    selectHasWeightRangeWarningByAuctionPenId,
  ],
  (
    auctionPens,
    dentitionWarningsByAuctionPenId,
    weightRangeWarningsByAuctionPenId,
  ) => {
    return Object.entries(auctionPens).reduce(
      (acc, [auctionPenId, _ignored]) => {
        const warnings = [];
        const dentitionWarning = dentitionWarningsByAuctionPenId[auctionPenId];
        if (dentitionWarning) {
          warnings.push("Dentition");
        }
        const weightRangeWarning =
          weightRangeWarningsByAuctionPenId[auctionPenId];
        if (weightRangeWarning) {
          warnings.push("Weight Range");
        }
        acc[auctionPenId] = warnings;
        return acc;
      },
      {},
    );
  },
);

export const selectIsUnderValidationThersholdBySaleLotId = createSelector(
  [
    getSaleLots,
    selectDeploymentIdBySaleLotIdLookup,
    selectValidationOverridesByDeploymentIdLookup,
  ],
  (saleLots, deploymentIdBySaleLotId, deploymentValidations) =>
    Object.entries(saleLots).reduce((acc, [saleLotId, saleLot]) => {
      const deploymentId = deploymentIdBySaleLotId[saleLotId];
      const validations = deploymentValidations[deploymentId] || {};
      acc[saleLotId] = Object.values(validations).reduce(
        (validationAcc, validation) => {
          if (validation) {
            validationAcc[camelCase(validation.validationName)] =
              saleLot.quantity <= validation.headThreshold;
          }
          return validationAcc;
        },
        {},
      );
      return acc;
    }, {}),
);

export const getNASFieldWarningsByAuctionPenId = penId => state =>
  selectNASFieldWarningsByAuctionPenId(state)[penId] || [];

/**
 * Returns a List of Sale Lot Warnings, keyed by Sale Lot Id
 * Requires:
 *  - `Businesses`
 *  - `Consignments`
 *  - `DeploymentSales`
 *  - `Sale Lots`
 *  - `Properties`
 *  - `Scans`
 * @type {function(state): Object<string, Array<string>>}
 */
export const selectWarningsBySaleLotIdLookup = createSelector(
  [
    getSaleyardName,
    getSaleLots,
    getBusinesses,
    selectSingleWeighWeightBySaleLotIdLookup,
    getActiveRole,
    selectHasUnregisteredEidsBySaleLotIdLookup,
    selectHasSexWarningBySaleLotId,
    selectHasBreedWarningBySaleLotId,
    selectHasAgeWarningBySaleLotId,
    selectHasWeightRangeWarningBySaleLotId,
    selectHasDentitionWarningBySaleLotId,
    selectHasVendorSplitsQuantityWarningBySaleLotId,
    selectHasVendorSplitsMissingVendorWarningBySaleLotId,
  ],
  (
    saleyardName,
    saleLots,
    businesses,
    singleWeighWeight,
    role,
    hasUnregisteredEidBySaleLotId,
    hasSexWarningBySaleLotId,
    hasBreedWarningBySaleLotId,
    hasAgeWarningBySaleLotId,
    hasWeightRangeWarningBySaleLotId,
    hasDentitionWarningBySaleLotId,
    hasVendorSplitsQuantityWarningBySaleLotId,
    hasVendorSplitsMissingVendorWarningBySaleLotId,
  ) =>
    Object.entries(saleLots).reduce((acc, [saleLotId, saleLot]) => {
      const warningsList = [];
      if (
        hasVendorSplitsQuantityWarningBySaleLotId[saleLotId] &&
        role.type === userTypes.LIVESTOCK_AGENT
      ) {
        warningsList.push(SaleLotWarning.VENDOR_SPLIT_LOTS_UNBALANCED);
      }

      if (
        hasVendorSplitsMissingVendorWarningBySaleLotId[saleLotId] &&
        role.type === userTypes.LIVESTOCK_AGENT
      ) {
        warningsList.push(
          SaleLotWarning.PRIMARY_VENDOR_IS_NOT_PART_OF_VENDOR_SPLIT_LOTS,
        );
      }

      if (hasSexWarningBySaleLotId[saleLotId]) {
        warningsList.push(SaleLotWarning.NO_SEX);
      }
      if (
        hasBreedWarningBySaleLotId[saleLotId] &&
        role.type === userTypes.LIVESTOCK_AGENT
      ) {
        warningsList.push(SaleLotWarning.NO_BREED);
      }
      if (hasAgeWarningBySaleLotId[saleLotId]) {
        warningsList.push(SaleLotWarning.NO_AGE);
      }
      if (
        hasDentitionWarningBySaleLotId[saleLotId] &&
        role.type === userTypes.LIVESTOCK_AGENT
      ) {
        warningsList.push(SaleLotWarning.NO_DENTITION);
      }
      if (
        hasWeightRangeWarningBySaleLotId[saleLotId] &&
        role.type === userTypes.LIVESTOCK_AGENT
      ) {
        warningsList.push(SaleLotWarning.NO_WEIGHT_RANGE);
      }

      if (saleLot.buyer_id) {
        const {
          emailRecipients = EMPTY_ARRAY,
          otherEmailRecipients = EMPTY_ARRAY,
          businessUsers = EMPTY_ARRAY,
        } = businesses[saleLot.buyer_id] || EMPTY_OBJECT;

        if (
          emailRecipients.filter(er => er.email).length === 0 &&
          otherEmailRecipients.filter(er => er.email).length === 0 &&
          // FIXME the BusinessUser counting here is wrong here - AG-5618
          //  It doesn't take into account either of:
          //   - Saleyard.enable_automatic_buyer_portal (count all BusinessUsers when true for yard)
          //   - BusinessUser.restrict_to_listed_sale_yards (always count this BusinessUser when False)
          //  both of which aren't currently available to frontend.
          businessUsers.filter(bu => bu.saleYards.includes(saleyardName))
            .length === 0
        ) {
          warningsList.push(SaleLotWarning.NO_RECIPIENTS);
        }
      }

      if (saleLot.thirdPartyId) {
        const {
          emailRecipients = EMPTY_ARRAY,
          otherEmailRecipients = EMPTY_ARRAY,
          businessUsers = EMPTY_ARRAY,
        } = businesses[saleLot.thirdPartyId] || EMPTY_OBJECT;
        if (
          emailRecipients.filter(er => er.email).length === 0 &&
          otherEmailRecipients.filter(er => er.email).length === 0 &&
          // FIXME the BusinessUser counting here is wrong here - AG-5618 - see above.
          businessUsers.filter(bu => bu.saleYards.includes(saleyardName))
            .length === 0
        ) {
          warningsList.push(SaleLotWarning.THIRD_PARTY_EMAIL_MISSING);
        }
      }

      const totalSingleWeighedWeight = singleWeighWeight[saleLotId];
      const saleLotWeight = saleLot.total_mass_grams;
      if (
        totalSingleWeighedWeight > 0 &&
        totalSingleWeighedWeight !== saleLotWeight &&
        role.type === userTypes.SALEYARD_ADMIN
      ) {
        warningsList.push(SaleLotWarning.SINGLE_WEIGH_SUM);
      }

      if (hasUnregisteredEidBySaleLotId[saleLotId]) {
        warningsList.push(SaleLotWarning.UNREGISTERED_EID);
      }

      acc[saleLotId] = warningsList;

      return acc;
    }, {}),
);

/**
 * Returns a list of Sale Lot Exceptions, and the associated Sale Lot Id for each applicable Sale Lot, keyed by Deployment Sale Id
 * Requires:
 *  - `Consignments`
 *  - `Properties`
 *  - `Sales`
 *  - `Sale Lots`
 * @type {function(state): Object<string, Array<{ saleLotId: string, exceptions: Array<string> }>>}
 */
export const selectSaleLotWarningsByDeploymentSaleIdLookup = createSelector(
  [
    selectCurrentDeploymentSaleIdsList,
    selectSaleLotIdsByDeploymentSaleIdLookup,
    selectWarningsBySaleLotIdLookup,
  ],
  (deploymentSaleIds, saleLotIdsByDeploymentSale, warningsBySaleLotId) =>
    deploymentSaleIds.reduce((acc, deploymentSaleId) => {
      const saleLotIds = saleLotIdsByDeploymentSale[deploymentSaleId] || [];

      const saleLotWarningsList = saleLotIds.reduce((acc, saleLotId) => {
        const warnings = warningsBySaleLotId[saleLotId];
        if (warnings.length > 0) {
          acc.push({ saleLotId, warnings });
        }
        return acc;
      }, []);

      acc[deploymentSaleId] =
        saleLotWarningsList.length > 0 ? saleLotWarningsList : EMPTY_ARRAY;
      return acc;
    }, {}),
);

/**
 * Returns a List of Consignment Warnings, keyed by Consignment Id
 * Requires:
 *  - `Consignments`
 *  - `Sales`
 * @type {function(state): Object<string, Array<string>>}
 */
export const selectWarningsByConsignmentIdLookup = createSelector(
  [
    getConsignments,
    state => getCurrentSale(state).species_id,
    selectIsScanRateCompliantByConsignmentIdLookup,
    selectSaleyardScanEidsByConsignmentIdLookup,
    selectHasUnregisteredEidsByConsignmentIdLookup,
  ],
  (
    consignments,
    speciesId,
    isScanRateCompliantByConsignmentId,
    saleyardScanEidsByConsignmentIdLookup,
    hasUnregisteredEidsByConsignmentId,
  ) =>
    Object.entries(consignments).reduce((acc, [consignmentId, consignment]) => {
      const warningsList = [];

      const nvdQuantity = consignment.quantity_NVD;
      if (
        typeof nvdQuantity === "number" &&
        consignment.quantity !== nvdQuantity
      ) {
        warningsList.push(ConsignmentWarnings.NVD_HC_MISMATCH);
      }

      if (speciesId !== Species.CATTLE) {
        // Scan Rate Compliance is an exception for Cattle, but a warning for other species
        if (!isScanRateCompliantByConsignmentId[consignmentId]) {
          warningsList.push(ConsignmentWarnings.SCAN_RATE_COMPLIANCE);
        }
      }

      const saleyardScans =
        saleyardScanEidsByConsignmentIdLookup[consignmentId];

      // The selector above sometimes returns `[undefined]`
      const hasSaleyardScans =
        saleyardScans?.length > 0 && saleyardScans.some(s => s);

      if (hasSaleyardScans) {
        warningsList.push(ConsignmentWarnings.CONSIGNMENT_SCANS_FOUND);
      }

      if (hasUnregisteredEidsByConsignmentId[consignmentId]) {
        warningsList.push(ConsignmentWarnings.UNREGISTERED_EID);
      }

      acc[consignmentId] = warningsList.length > 0 ? warningsList : EMPTY_ARRAY;
      return acc;
    }, {}),
);

/**
 * Returns a list of Consignment Warnings, and the associated Consignment Id for each applicable Consignment, keyed by Deployment Sale Id
 * Requires:
 *  - `Consignments`
 * @type {function(state): Object<string, Array<{ consignmentId: string, exceptions: Array<string> }>>}
 */
export const selectConsignmentWarningsByDeploymentSaleIdLookup = createSelector(
  [
    selectCurrentDeploymentSaleIdsList,
    selectConsignmentIdsByDeploymentSaleIdLookup,
    selectWarningsByConsignmentIdLookup,
  ],
  (
    deploymentSaleIds,
    consignmentIdsByDeploymentSaleId,
    warningsByConsignmentId,
  ) =>
    deploymentSaleIds.reduce((acc, deploymentSaleId) => {
      const consignmentIds =
        consignmentIdsByDeploymentSaleId[deploymentSaleId] || [];

      const warningsByConsignmentList = consignmentIds.reduce(
        (acc, consignmentId) => {
          const warnings = warningsByConsignmentId[consignmentId];
          if (warnings.length > 0) {
            acc.push({ consignmentId, warnings });
          }
          return acc;
        },
        [],
      );

      acc[deploymentSaleId] =
        warningsByConsignmentList.length > 0
          ? warningsByConsignmentList
          : EMPTY_ARRAY;

      return acc;
    }, {}),
);

// Temporary Businesses created and used in the current Sale, with their
// warnings.
export const selectTemporaryBusinessWarningsInCurrentSale = createSelector(
  [selectUsedTemporaryBusinessesInCurrentSale],
  usedTemporaryBusinesses => {
    const businessWarnings = usedTemporaryBusinesses.map(business => ({
      businessId: business.id,
      warnings: [TEMPORARY_BUSINESS_WARNING],
    }));
    return businessWarnings.length > 0 ? businessWarnings : EMPTY_ARRAY;
  },
);

export const getConsignmentWarningsByDeploymentSaleId =
  deploymentSaleId => state =>
    selectConsignmentWarningsByDeploymentSaleIdLookup(
      state,
      getLivestockSaleId(),
    )[deploymentSaleId];

export const getSaleLotWarningsByDeploymentSaleId = deploymentSaleId => state =>
  selectSaleLotWarningsByDeploymentSaleIdLookup(state, getLivestockSaleId())[
    deploymentSaleId
  ];

export const getHasConsignmentWarningsByDeploymentSaleId =
  deploymentSaleId => state =>
    selectConsignmentWarningsByDeploymentSaleIdLookup(
      state,
      getLivestockSaleId(),
    )[deploymentSaleId].length > 0;

export const getHasSaleLotWarningsByDeploymentSaleId =
  deploymentSaleId => state =>
    selectSaleLotWarningsByDeploymentSaleIdLookup(state, getLivestockSaleId())[
      deploymentSaleId
    ].length > 0;

export const getHasWarningsByDeploymentSaleId = deploymentSaleId => state =>
  getHasConsignmentWarningsByDeploymentSaleId(deploymentSaleId)(state) ||
  getHasSaleLotWarningsByDeploymentSaleId(deploymentSaleId)(state);

export const getHasTemporaryBusinessWarningsInCurrentSale = state =>
  selectTemporaryBusinessWarningsInCurrentSale(state).length > 0;

// Matches the colour of the Exceptions tab on the Sale summary: error where a
// Deployment Sale has exceptions, gold where it only has warnings.
export const getAttentionColorInCurrentSale = (
  state,
  hasTemporaryBusinesses,
) => {
  const deploymentSaleIds = selectCurrentDeploymentSaleIdsList(state);
  if (
    deploymentSaleIds.some(deploymentSaleId =>
      getHasExceptionsByDeploymentSaleId(deploymentSaleId)(state),
    )
  ) {
    return "error";
  }
  if (
    deploymentSaleIds.some(deploymentSaleId =>
      getHasWarningsByDeploymentSaleId(deploymentSaleId)(state),
    ) ||
    (hasTemporaryBusinesses &&
      getHasTemporaryBusinessWarningsInCurrentSale(state))
  ) {
    return "gold";
  }
  return null;
};

// For Sale overviews that only surface temporary Business warnings.
export const getTemporaryBusinessWarningsAttentionColorInCurrentSale = (
  state,
  hasTemporaryBusinesses,
) =>
  hasTemporaryBusinesses && getHasTemporaryBusinessWarningsInCurrentSale(state)
    ? "gold"
    : null;

export const selectWarningsForEqxDataByDeploymentSaleId = createSelector(
  [
    selectCurrentDeploymentSaleIdsList,
    selectSaleLotWarningsByDeploymentSaleIdLookup,
    selectDeploymentByDeploymentSaleIdLookup,
    selectValidationOverridesByDeploymentIdLookup,
  ],
  (
    deploymentSaleIds,
    lotWarningsByDeploymentSaleId,
    deploymentByDeploymentSaleId,
    deploymentValidationsByDeploymentId,
  ) =>
    deploymentSaleIds.reduce((acc, deploymentSaleId) => {
      const deployment = deploymentByDeploymentSaleId[deploymentSaleId];
      const validations =
        deploymentValidationsByDeploymentId[deployment?.id] || [];
      const lotWarnings = lotWarningsByDeploymentSaleId[deploymentSaleId];

      const hasBlockedDentitionWarning =
        validations?.missingDentition?.validationLevel ===
          WarningLevel.WARNING_AND_BLOCK &&
        lotWarnings.find(lotWarning =>
          lotWarning.warnings?.includes(SaleLotWarning.NO_DENTITION),
        );

      const hasBlockedWeightRangeWarning =
        validations?.missingEstimatedWeight?.validationLevel ===
          WarningLevel.WARNING_AND_BLOCK &&
        lotWarnings.find(lotWarning =>
          lotWarning.warnings?.includes(SaleLotWarning.NO_WEIGHT_RANGE),
        );
      const warnings = [];
      if (hasBlockedDentitionWarning) {
        warnings.push("Dentition");
      }
      if (hasBlockedWeightRangeWarning) {
        warnings.push("Est Avg Weight");
      }
      acc[deploymentSaleId] = warnings;
      return acc;
    }, {}),
);
